import { BehaviorSubject, timer, Subscription, switchMap, from, map, combineLatest, distinctUntilChanged, catchError, of } from "rxjs";
import p5 from "p5";

import { REFRESH_RATE, metroSystem } from "@services/system.service.ts";
import { getElement } from "@helpers/utils.ts";

import TableBoard from "@components/tableBoard.ts";
import { ColumnAlign, ColumnType } from "@config/board.config";
import { DotColor, LineColor } from "@models/colors.model";
import { TableRow } from "@models/board.model";
import { ArrivalTracker, TrackedArrivalMap } from "@helpers/arrivalTracker";

const HEADING_TEXT = "Stations";
const ERROR_MSG_ARRAY = [":(", "", "Error", "?"];
const EMPTY_MSG_ARRAY = [":)", " ", "No trains!", " "];
const HEADER_ARRAY = ["LINE", "CAR", "DEST", "MIN"];
const DEFAULT_ID = "college-park-u-of-md";
const DEFAULT_PLATFORM = "E09";
const DEFAULT_TRACK: Track = 1;
const CAR_COL = 1;
const HIDDEN_COL = CAR_COL;
const TIME_COL = 3;

enum ActionType {
  ChangeStation,
  SwitchPlatform,
  SwitchTrack,
}

type Action =
  | { type: ActionType.ChangeStation; stationId: string }
  | { type: ActionType.SwitchPlatform }
  | { type: ActionType.SwitchTrack };

type Track = 1 | 2;

type Selection = {
  stationId: string;
  platformCode: string;
  track: Track;
};

const selection$ = new BehaviorSubject<Selection>({
  stationId: DEFAULT_ID,
  platformCode: DEFAULT_PLATFORM,
  track: DEFAULT_TRACK,
});

let stationBoard: TableBoard | null = null;
let subscription = new Subscription();

const transferIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 2 20 20" height="1.25rem" width="1.25rem" fill="currentColor"><path d="M12,15q-1.25,0-2.13-0.88T9,12T9.88,9.88T12,9t2.13,0.88T15,12t-0.88,2.13T12,15ZM8.1,21.21q-1.82-0.79-3.17-2.14Q3.58,17.73 2.79,15.9T2,12Q2,9.92 2.79,8.1Q3.58,6.27 4.93,4.93Q6.28,3.57 8.1,2.79Q9.93,2 12,2t3.9,0.79t3.17,2.14q1.35,1.35 2.14,3.17Q22,9.92 22,12t-0.79,3.9t-2.14,3.17q-1.35,1.35-3.17,2.14T12,22q-2.07,0-3.9-0.79ZM12,20q3.33,0 5.66-2.34T20,12q0-3.33-2.34-5.66T12,4Q8.68,4 6.34,6.34T4,12t2.34,5.66T12,20Zm0-8Zm3.54,3.54Q17,14.08 17,12q0-2.08-1.46-3.54T12,7Q9.93,7 8.46,8.46T7,12t1.46,3.54T12,17t3.54-1.46Z"/></svg>`;
const terminusIcons = (lines: string[]) =>
  lines
    .map(
      (lineCode) =>
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 2 20 20" height="1.25rem" width="1.25rem" fill="${LineColor.get(lineCode)}">
        <path d="M12,22q-2.07,0-3.9-0.79q-1.82-0.79-3.17-2.14Q3.58,17.73 2.79,15.9T2,12Q2,9.92 2.79,8.1Q3.58,6.27 4.93,4.93Q6.28,3.57 
        8.1,2.79Q9.93,2 12,2t3.9,0.79t3.17,2.14q1.35,1.35 2.14,3.17Q22,9.92 22,12t-0.79,3.9t-2.14,3.17q-1.35,1.35-3.17,2.14T12,22Z"/></svg>`,
    )
    .join("");

const template = () => {
  return `
    <div class="station-label"></div>
    <div class="board-target"></div>
    <div class="switch-target">
      <button class="btn-tracks">Switch Tracks</button>
      <button class="btn-platforms" hidden>Switch Platforms</button>
    </div>
    <div class="list-target"></div>
  `;
};

/**
 * Renders the station view and initializes its board and subscriptions.
 */
export function render(): void {
  getElement(".header-target .heading-text").textContent = HEADING_TEXT;
  getElement(".content-target").innerHTML = template();

  renderStationList();
  attachEventListeners();

  // Construct board
  const boardTarget: HTMLElement = getElement(".board-target");
  const board = new TableBoard(boardTarget, {
    header: HEADER_ARRAY,
    rows: [],
    layout: [
      { type: ColumnType.Hug, align: ColumnAlign.Right },
      { type: ColumnType.Hug, align: ColumnAlign.Center },
      { type: ColumnType.Fill, align: ColumnAlign.Left },
      { type: ColumnType.Hug, align: ColumnAlign.Right },
    ],
    updateCol: TIME_COL,
    hiddenCol: HIDDEN_COL,
    bumper: { col: 0, colorFor: (line) => LineColor.get(line) },
    charColor: (col, char) => (col === CAR_COL && char === "8" ? DotColor.Highlight : undefined),
  });
  new p5(board.sketch, boardTarget);
  stationBoard = board;

  // Handle selection changes and data updates
  subscription.unsubscribe();
  subscription = subscribeToSelectionChanges();
  subscription.add(subscribeToDataUpdates(board));
}

/**
 * Stops reactive updates and destroys the current station board.
 */
export function pause(): void {
  subscription.unsubscribe();
  stationBoard?.destroy$.next();
  stationBoard = null;
}

function attachEventListeners(): void {
  const trackButton = getElement(".btn-tracks");
  trackButton.addEventListener("click", () => dispatchAction({ type: ActionType.SwitchTrack }));

  const platformButton = getElement(".btn-platforms");
  platformButton.addEventListener("click", () => dispatchAction({ type: ActionType.SwitchPlatform }));

  const listElement = getElement(".list-target");
  listElement.addEventListener("click", (event) => {
    const stationButton = (event.target as Element).closest<HTMLElement>(".btn-station");
    const stationId = stationButton?.dataset.stationId;
    if (stationId) {
      dispatchAction({ type: ActionType.ChangeStation, stationId });
    }
  });
}

/**
 * Applies an action to the current selection and emits the new state if changed.
 *
 * @param action - The action to apply.
 */
function dispatchAction(action: Action): void {
  const current = selection$.value;
  const next = updateSelection(current, action);

  if (next !== current) {
    selection$.next(next);
  }
}

/**
 * Updates the selection state for the given user action.
 *
 * Switching stations resets the platform to the station's first code (e.g. "E09").
 * Platform switching has no effect when the station has only one code.
 *
 * @param selection - The current selection state.
 * @param action - The user action to apply.
 * @returns The updated selection, or the original object if no change is needed.
 */
function updateSelection(selection: Selection, action: Action): Selection {
  switch (action.type) {
    case ActionType.ChangeStation: {
      if (action.stationId === selection.stationId) {
        return selection;
      }

      const station = metroSystem.getStation(action.stationId);
      return { ...selection, stationId: station.id, platformCode: station.codes[0] };
    }

    case ActionType.SwitchTrack:
      return { ...selection, track: selection.track === 1 ? 2 : 1 };

    case ActionType.SwitchPlatform: {
      const { codes } = metroSystem.getStation(selection.stationId);
      if (codes.length < 2) {
        return selection;
      }

      const next = codes[(codes.indexOf(selection.platformCode) + 1) % codes.length];
      return { ...selection, platformCode: next };
    }
  }
}

/**
 * Polls the API for the latest train positions.
 * Converts fetch failures to null so polling can continue.
 */
function pollArrivals(stationId: string) {
  return timer(0, REFRESH_RATE).pipe(
    switchMap(() =>
      from(metroSystem.fetchArrivals(stationId)).pipe(
        catchError(() => {
          return of(null);
        }),
      ),
    ),
  );
}

/**
 * Updates the station label, selected station button, and platform switch
 * visibility whenever the selected station changes.
 *
 * @returns The subscription that manages these UI updates.
 */
function subscribeToSelectionChanges() {
  return selection$
    .pipe(
      map((s) => s.stationId),
      distinctUntilChanged(),
    )
    .subscribe((id) => {
      const station = metroSystem.getStation(id);

      getElement(".station-label").textContent = station.name;
      getElement(".btn-platforms").toggleAttribute("hidden", station.codes.length < 2);
      getElement(".btn-station.selected").classList.remove("selected");
      getElement(`.btn-station[data-station-id="${CSS.escape(id)}"]`).classList.add("selected");
    });
}

/**
 * Polls arrival data for the selected station and updates the board when
 * arrivals, track selection, or platform selection changes.
 *
 * Switching stations restarts polling for the new station.
 *
 * @param board - The station board to update.
 * @returns The subscription that manages polling and board updates.
 */
function subscribeToDataUpdates(board: TableBoard) {
  const tracker = new ArrivalTracker();

  return selection$
    .pipe(
      map((selection) => selection.stationId),
      distinctUntilChanged(),
      switchMap((stationId) => {
        tracker.reset();
        const arrivals$ = pollArrivals(stationId).pipe(map((arrivals) => tracker.update(arrivals)));

        return combineLatest([selection$, arrivals$]).pipe(
          map(([selection, arrivals]) => ({
            dataId: selection.stationId,
            viewKey: `${selection.platformCode}|${selection.track}`,
            messageTable: getMessageTable(selection.platformCode, selection.track, arrivals),
          })),
        );
      }),
    )
    .subscribe((data) => board.data$.next(data));
}

/**
 * Builds the message rows displayed on the station board.
 *
 * Returns an error row when arrival data is unavailable, or an empty-state
 * row when no arrivals match the selected platform and track.
 *
 * @param platformCode - The selected station platform code.
 * @param trackIndex - The selected track number.
 * @param arrivals - Arrival data indexed by platform and track, or null if unavailable.
 * @returns The rows to display on the station board.
 */
function getMessageTable(platformId: string, trackIndex: number, arrivals: TrackedArrivalMap): TableRow[] {
  const platform = arrivals?.get(platformId);
  if (!platform) {
    return [{ id: 500, cells: ERROR_MSG_ARRAY }];
  }

  const group = platform.get(String(trackIndex));
  if (!group?.length) {
    return [{ id: 200, cells: EMPTY_MSG_ARRAY }];
  }

  return group.map(({ id, line, numCars, destName, min }) => ({ id: id, cells: [line, numCars, destName, min] }));
}

function renderStationList() {
  try {
    const listTarget = getElement(".list-target");
    const sortedStations = [...metroSystem.stations].sort((a, b) => a.name.localeCompare(b.name));

    for (const station of sortedStations) {
      const classes = ["btn-dark", "btn-station"];
      station.transfer ? classes.push("transfer") : station.terminus ? classes.push("terminus") : null;
      const icons = station.transfer ? transferIcon : station.terminus ? terminusIcons(station.lines || []) : "";

      const button = document.createElement("button");
      button.classList.add(...classes);
      button.textContent = station.name;
      button.innerHTML = `${icons ? "<span class='station-icons'>" + icons + "</span>" : ""}<span class="station-name">${station.name}</span>`;
      button.dataset.stationId = station.id;

      listTarget.appendChild(button);

      if (station.id === DEFAULT_ID) {
        button.classList.add("selected");
      }
    }
  } catch (error) {
    console.error("Failed to draw station list:", error);
    const container = getElement(".list-target");
    container.innerHTML = `<div class="error">Failed to load station data</div>`;
  }
}
